import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { DocumentExportActions } from './DocumentExportActions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export const InternalTransferDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/internal-transfers/${id}`, getAuthHeaders())
      .then(r => setDoc(r.data))
      .catch(() => setError('Failed to load transfer'))
      .finally(() => setLoading(false));
  }, [id]);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="h-10 w-10 animate-spin text-orange-500" /></div>;
  if (error || !doc) return <div className="flex items-center gap-2 text-red-600 p-8"><AlertCircle className="h-5 w-5" />{error || 'Transfer not found'}</div>;

  const exportConfig = {
    title: 'Internal Transfer', number: doc.transfer_number,
    metadata: [
      { label: 'Transfer Number', value: doc.transfer_number || '-' }, { label: 'Date', value: formatDate(doc.transfer_date) },
      { label: 'From Company', value: doc.from_company_name || '-' }, { label: 'To Company', value: doc.to_company_name || '-' },
      { label: 'Sale Invoice', value: doc.sale_invoice_number || '-' }, { label: 'Purchase Bill', value: doc.purchase_bill_number || '-' },
    ],
    columns: [
      { label: '#', value: (_, index) => index + 1, type: 'number' }, { label: 'Product', value: item => item.product_name || '-' },
      { label: 'Product Code', value: item => item.product_code || '-' }, { label: 'Qty', value: item => Number(item.quantity || 0), type: 'number' },
      { label: 'Purchase Rate', value: item => Number(item.purchase_rate || 0), type: 'number' }, { label: 'Display Rate', value: item => Number(item.display_rate || 0), type: 'number' },
      { label: 'Sale Rate', value: item => Number(item.sale_rate || 0), type: 'number' }, { label: 'Total', value: item => Number(item.total_amount || 0), type: 'number' },
      { label: 'Item IDs', value: item => (item.item_ids || []).join(', ') },
      { label: 'Asset IDs', value: item => (item.asset_ids || []).join(', ') || '-' },
    ],
    rows: doc.line_items || [],
    totals: [{ label: 'Total Transfer Value', value: `INR ${Number(doc.total || 0).toFixed(2)}`, rawValue: Number(doc.total || 0) }],
    shareLines: [`From: ${doc.from_company_name || '-'}`, `To: ${doc.to_company_name || '-'}`, `Date: ${formatDate(doc.transfer_date)}`, `Total Value: INR ${Number(doc.total || 0).toFixed(2)}`],
  };

  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate('/admin/inventory/internal-transfers')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 print:hidden">
        <ArrowLeft className="h-4 w-4" /> Back to Internal Transfers
      </button>

      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Internal Transfer</h1>
        <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold uppercase rounded">Completed</span>
      </div>

      <div className="mb-8"><DocumentExportActions config={exportConfig} testIdPrefix="internal-transfer" /></div>

      {/* Header Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <div><span className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Transfer No.</span><span className="font-mono font-bold text-slate-900">{doc.transfer_number}</span></div>
        <div><span className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Date</span><span className="text-slate-900">{formatDate(doc.transfer_date)}</span></div>
        <div><span className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Total Value</span><span className="font-bold text-slate-900">₹{(doc.total || 0).toLocaleString()}</span></div>
        <div><span className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Notes</span><span className="text-slate-600 text-sm">{doc.notes || '-'}</span></div>
      </div>

      {/* From → To */}
      <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <div className="flex-1 text-center">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-1">From Company</p>
          <p className="text-lg font-bold text-slate-900">{doc.from_company_name}</p>
          <p className="text-xs text-slate-400">Stock deducted</p>
        </div>
        <div className="flex-shrink-0">
          <ArrowRightLeft className="h-8 w-8 text-orange-500" />
        </div>
        <div className="flex-1 text-center">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-1">To Company</p>
          <p className="text-lg font-bold text-slate-900">{doc.to_company_name}</p>
          <p className="text-xs text-slate-400">Stock added</p>
        </div>
      </div>

      {/* Auto-created Documents */}
      {doc.sale_invoice_id && (
        <div className="bg-green-50 border border-green-200 rounded-sm p-4 mb-3 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-green-700 uppercase mb-0.5">Auto-created Sale Invoice</p>
            <p className="font-mono font-bold text-green-900">{doc.sale_invoice_number}</p>
            <p className="text-xs text-green-600">Created for {doc.from_company_name}</p>
          </div>
          <button onClick={() => navigate(`/admin/sale/invoices/${doc.sale_invoice_id}`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-bold uppercase rounded hover:bg-green-700 print:hidden">
            <ExternalLink className="h-3.5 w-3.5" /> View Invoice
          </button>
        </div>
      )}
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-blue-700 uppercase mb-0.5">Auto-created Purchase Bill</p>
          <p className="font-mono font-bold text-blue-900">{doc.purchase_bill_number}</p>
          <p className="text-xs text-blue-600">Created for {doc.to_company_name}</p>
        </div>
        {doc.purchase_bill_id && (
          <button onClick={() => navigate(`/admin/purchase/bills/${doc.purchase_bill_id}`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-bold uppercase rounded hover:bg-blue-700 print:hidden">
            <ExternalLink className="h-3.5 w-3.5" /> View Bill
          </button>
        )}
      </div>

      {/* Line Items */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-bold text-slate-900 uppercase tracking-tight text-sm">Transferred Items</h3>
        </div>
        <div className="hidden md:grid grid-cols-8 gap-4 px-6 py-3 border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-500 uppercase">
          <div>Product</div><div>Qty</div><div>Purchase Rate</div><div>Display Rate</div><div>Sale Rate</div><div>Total</div><div>Item IDs</div><div>Asset IDs</div>
        </div>
        {(doc.line_items || []).map((li, idx) => (
          <div key={idx} className="grid grid-cols-1 md:grid-cols-8 gap-4 px-6 py-4 border-b border-slate-100 items-start">
            <div>
              <p className="font-medium text-slate-900 text-sm">{li.product_name}</p>
              <p className="text-xs text-slate-400 font-mono">{li.product_code}</p>
            </div>
            <div className="text-slate-700">{li.quantity}</div>
            <div className="text-slate-700">₹{(li.purchase_rate || 0).toLocaleString()}</div>
            <div className="text-slate-700">₹{(li.display_rate || 0).toLocaleString()}</div>
            <div className="text-slate-700">₹{(li.sale_rate || 0).toLocaleString()}</div>
            <div className="font-medium text-slate-900">₹{(li.total_amount || 0).toLocaleString()}</div>
            <div className="flex flex-wrap gap-1">
              {(li.item_ids || []).map(iid => (
                <span key={iid} className="inline-block px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-mono">{iid}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-1">
              {(li.asset_ids || []).length > 0 ? (li.asset_ids || []).map(assetId => (
                <span key={assetId} className="inline-block px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-mono">{assetId}</span>
              )) : <span className="text-slate-400">-</span>}
            </div>
          </div>
        ))}
        <div className="flex justify-end px-6 py-4">
          <div className="text-right">
            <span className="text-sm text-slate-500 uppercase font-semibold mr-4">Total Transfer Value</span>
            <span className="text-xl font-bold text-slate-900">₹{(doc.total || 0).toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
